import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Search, X, ChevronDown, Download, FileSpreadsheet, Package } from "lucide-react";
import { sharedExportEntregasExcel } from "../utils/shared-excel";
import {
    obtenerEstadoWorkflow,
    obtenerSubEstadoWorkflow,
    obtenerEstadoPagoWorkflow,
} from "../utils/shared-entregas-workflow";
import { ID_EMPRESA } from "../utils/const/empresa-const";
import { sharedObtenerIdEmpresa } from "../utils/shared-functions";

/** Visible columns in the desktop delivery table (for colspan). */
const DESKTOP_COLUMN_COUNT = 18;

function formatCoordenadas(value) {
    const s = value == null ? "" : String(value).trim();
    if (!s || s.includes("undefined")) return "—";
    return s;
}

function displayValue(value) {
    if (value == null || String(value).trim() === "") return "—";
    return value;
}

function Badge({ children, tone = "neutral" }) {
    const tones = {
        success: "bg-emerald-100 text-emerald-800",
        warning: "bg-amber-100 text-amber-900",
        danger: "bg-red-100 text-red-800",
        info: "bg-sky-100 text-sky-900",
        neutral: "bg-gray-100 text-gray-700",
    };
    return (
        <span
            className={`inline-flex max-w-full items-center justify-center rounded-md px-2 py-0.5 text-[11px] font-semibold leading-tight ${tones[tone] ?? tones.neutral}`}
        >
            {children}
        </span>
    );
}

function estadoPagoTone(estado) {
    const e = String(estado ?? "").toUpperCase();
    if (e.includes("DEBE") || e.includes("PEND")) return "warning";
    if (e.includes("CANCEL") || e.includes("PAG")) return "success";
    return "neutral";
}

function finalizadoTone(item) {
    if (item?.finalizado === 1) return "success";
    const label = String(item?.finalizadoLabel ?? "").toLowerCase();
    if (label === "si" || label === "sí" || label === "yes") return "success";
    return "neutral";
}

function formatNumber(value) {
    const num = Number(value);
    if (!Number.isFinite(num)) return "—";
    return num.toLocaleString("es-BO", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });
}

function getItemKey(item, index) {
    return item?.idDetallePedido ?? `${item?.numeroDocumento ?? "row"}-${index}`;
}

function getProductosList(item) {
    return Array.isArray(item?.productos) ? item.productos : [];
}

/** Build searchable text from delivery fields and nested productos. */
function itemSearchText(item) {
    const productosText = getProductosList(item)
        .map((p) =>
            [p.codigoProducto, p.nombreProducto, p.unidadMedida, p.moneda]
                .filter((v) => v != null && String(v).trim() !== "")
                .join(" ")
        )
        .join(" ");

    return [
        item.labelPlaca,
        item.valuePlaca,
        item.numeroDocumento,
        item.cliente,
        item.geocerca,
        item.tipoServicio,
        item.estadoPago,
        item.workflow,
        item.finalizadoLabel,
        item.fechaProcesoWorkflow,
        item.coordenadasCliente,
        item.coordenadasInicioAtencion,
        item.coordenadasFinAtencion,
        item.latitudCliente,
        item.longitudCliente,
        productosText,
    ]
        .filter((v) => v != null && String(v).trim() !== "")
        .join(" ")
        .toLowerCase();
}

function matchesFilter(item, query) {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return itemSearchText(item).includes(q);
}

/**
 * Productos delivered — table layout suitable for screen + Excel/CSV export.
 */
function ProductosEntregasPanel({ item, expanded, onToggle, t }) {
    const productos = getProductosList(item);
    const count = productos.length;
    const totalMonto = productos.reduce((sum, p) => sum + (Number(p.monto) || 0), 0);
    const moneda = productos.find((p) => p.moneda)?.moneda ?? "";

    return (
        <div
            className="border-t border-gray-200 bg-slate-50/80 px-3 py-2"
            data-export-section="productos"
            data-numero-documento={item?.numeroDocumento ?? ""}
        >
            <button
                type="button"
                onClick={onToggle}
                className="flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-sm font-medium text-gray-700 hover:bg-white/80 transition-colors"
                aria-expanded={expanded}
            >
                <span className="inline-flex items-center gap-2 min-w-0">
                    <Package className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
                    <span className="truncate">
                        {t("label_productos")}
                        <span className="ml-1 font-normal text-gray-500">({count})</span>
                    </span>
                </span>
                <ChevronDown
                    className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${expanded ? "rotate-180" : ""}`}
                    aria-hidden
                />
            </button>

            {expanded && (
                <div className="mt-2">
                    {count === 0 ? (
                        <p className="text-xs text-gray-500 py-2">{t("label_sin_datos")}</p>
                    ) : (
                        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
                            <table
                                className="entregas-productos-table w-full min-w-[720px] border-collapse text-left text-xs"
                                data-export-table="productos"
                            >
                                <thead>
                                    <tr className="border-b border-gray-200 bg-gray-50 text-[10px] font-bold uppercase tracking-wide text-gray-600">
                                        <th className="px-2 py-2 whitespace-nowrap">{t("column_codigo_producto")}</th>
                                        <th className="px-2 py-2 min-w-[12rem]">{t("column_nombre_producto")}</th>
                                        <th className="px-2 py-2 whitespace-nowrap">{t("column_unidad_medida")}</th>
                                        <th className="px-2 py-2 text-right whitespace-nowrap">{t("column_cantidad")}</th>
                                        <th className="px-2 py-2 text-right whitespace-nowrap">{t("column_peso")}</th>
                                        <th className="px-2 py-2 text-right whitespace-nowrap">{t("column_volumen")}</th>
                                        <th className="px-2 py-2 text-right whitespace-nowrap">{t("column_monto")}</th>
                                        <th className="px-2 py-2 whitespace-nowrap">{t("column_moneda")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {productos.map((p, idx) => (
                                        <tr
                                            key={p.idProducto ?? `${p.codigoProducto}-${idx}`}
                                            className="border-b border-gray-50 last:border-0 even:bg-gray-50/50"
                                            data-export-row="producto"
                                        >
                                            <td className="px-2 py-2 font-mono text-[11px] text-gray-800 whitespace-nowrap">
                                                {displayValue(p.codigoProducto)}
                                            </td>
                                            <td className="px-2 py-2 text-gray-900 break-words leading-snug">
                                                {displayValue(String(p.nombreProducto ?? "").trim())}
                                            </td>
                                            <td className="px-2 py-2 text-gray-700 whitespace-nowrap">
                                                {displayValue(p.unidadMedida)}
                                            </td>
                                            <td className="px-2 py-2 text-right tabular-nums text-gray-900">
                                                {formatNumber(p.cantidad)}
                                            </td>
                                            <td className="px-2 py-2 text-right tabular-nums text-gray-700">
                                                {formatNumber(p.peso)}
                                            </td>
                                            <td className="px-2 py-2 text-right tabular-nums text-gray-700">
                                                {formatNumber(p.volumen)}
                                            </td>
                                            <td className="px-2 py-2 text-right tabular-nums font-medium text-gray-900">
                                                {formatNumber(p.monto)}
                                            </td>
                                            <td className="px-2 py-2 text-gray-700 whitespace-nowrap">
                                                {displayValue(p.moneda)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="bg-gray-50 border-t border-gray-200 font-semibold text-gray-900">
                                        <td colSpan={6} className="px-2 py-2 text-right text-[11px] uppercase tracking-wide">
                                            {t("label_total")}
                                        </td>
                                        <td className="px-2 py-2 text-right tabular-nums">
                                            {formatNumber(totalMonto)}
                                        </td>
                                        <td className="px-2 py-2">{moneda}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Cell({ label, children, className = "", mono = false }) {
    return (
        <div
            className={`grid grid-cols-[minmax(7rem,38%)_1fr] gap-x-3 gap-y-0.5 border-b border-gray-100 py-2 last:border-0 sm:grid-cols-[minmax(8rem,32%)_1fr] ${className}`}
        >
            <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 sm:text-[11px]">
                {label}
            </span>
            <span
                className={`text-sm text-gray-900 break-words min-w-0 ${mono ? "font-mono text-xs" : ""}`}
            >
                {children}
            </span>
        </div>
    );
}

/**
 * Full responsive table: cards on mobile/tablet, data grid table on lg+.
 * @param {{
 *   items: Array<Record<string, unknown>>,
 *   exportMeta?: { reportName?: string, dateRangeLabel?: string, fileName?: string },
 * }} props
 */
export function TableEntregasTable({ items, exportMeta = {}, btnExportar = false }) {
    const { t } = useTranslation();
    const [filterQuery, setFilterQuery] = useState("");
    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const [expandedProductos, setExpandedProductos] = useState(() => new Set());
    const exportButtonRef = useRef(null);
    const exportMenuRef = useRef(null);
    const idEmpresa = sharedObtenerIdEmpresa();

    const toggleProductos = (rowKey) => {
        setExpandedProductos((prev) => {
            const next = new Set(prev);
            if (next.has(rowKey)) next.delete(rowKey);
            else next.add(rowKey);
            return next;
        });
    };

    const filteredItems = useMemo(() => {
        if (!Array.isArray(items)) return [];
        return items.filter((item) => matchesFilter(item, filterQuery));
    }, [items, filterQuery]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                exportMenuRef.current &&
                exportButtonRef.current &&
                !exportMenuRef.current.contains(event.target) &&
                !exportButtonRef.current.contains(event.target)
            ) {
                setExportMenuOpen(false);
            }
        };
        if (exportMenuOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [exportMenuOpen]);

    if (!Array.isArray(items) || items.length === 0) return null;

    const hasFilter = filterQuery.trim().length > 0;
    const total = items.length;
    const shown = filteredItems.length;
    const canExport = shown > 0;

    const handleExportPdfClick = () => {
        exportEntregasPdf(filteredItems);
        setExportMenuOpen(false);
    };

    const handleExportExcelClick = () => {
        exportEntregasExcel(filteredItems);
        setExportMenuOpen(false);
    };

    const exportEntregasExcel = async (rows) => {
        let fechaProceso = t("column_fecha_proceso");
        if (idEmpresa === ID_EMPRESA.ORANGE) {
            fechaProceso = t("column_fecha_entrega_conductor");
        }
        await sharedExportEntregasExcel(rows, {
            reportName: exportMeta.reportName ?? t("label_reporte_resumen_entregas"),
            dateRangeLabel: exportMeta.dateRangeLabel ?? "",
            fileName:
                exportMeta.fileName ??
                `resumen-entregas-${moment().format("YYYY-MM-DD-HHmm")}`,
            workflowLabels: {
                estadoAsignado: t("label_estado_asignado"),
                estadoEnProceso: t("label_estado_en_proceso"),
                empty: "",
            },
            labels: {
                placa: t("label_placa"),
                numeroDocumento: t("label_numero_documento"),
                cliente: t("label_cliente"),
                geocerca: t("label_geocerca"),
                tipoServicio: t("label_tipo_servicio"),
                estadoEntrega: t("label_estado"),
                subEstadoEntrega: t("label_sub_estado"),
                finalizado: t("label_finalizado"),
                estadoPagoWorkflow: t("label_estado_pago"),
                estadoPagoDetalle: t("label_detalle_estado_pago"),
                fechaEntregaProgramada: t("column_fecha_entrega_programada"),
                fechaEntregaExcel: t("column_fecha_entrega_excel"),
                fechaCarguio: t("column_fecha_carguio"),
                fechaProgramacionSalida: t("column_fecha_programacion_salida"),
                fechaProceso: fechaProceso,
                coordCliente: t("column_coordenadas_cliente"),
                // coordInicio: t("column_coordenadas_inicio_atencion"),
                // coordFin: t("column_coordenadas_fin_atencion"),
                codigoProducto: t("column_codigo_producto"),
                nombreProducto: t("column_nombre_producto"),
                unidadMedida: t("column_unidad_medida"),
                cantidad: t("column_cantidad"),
                peso: t("column_peso"),
                volumen: t("column_volumen"),
                monto: t("column_monto"),
                moneda: t("column_moneda"),
                fechaHoraInicioAtencion: t("column_fecha_hora_inicio_atencion"),
                fechaHoraFinAtencion: t("column_fecha_hora_fin_atencion"),
                nombreConductor: t("column_nombre_conductor"),
                almacen: t("column_almacen"),
            },
        });
    };

    /**
     * Mobile / tablet: card with label–value grid.
     */
    const TableEntregasCardMobile = ({ item, index, rowKey }) => {
        const placa = item.labelPlaca ?? item.valuePlaca ?? "—";
        const clienteColor =
            item.colorCliente && String(item.colorCliente).trim() !== ""
                ? String(item.colorCliente)
                : null;
        const productosExpanded = expandedProductos.has(rowKey);
        let fechaProceso = t("column_fecha_proceso");
        if (idEmpresa === ID_EMPRESA.ORANGE) {
            fechaProceso = t("column_fecha_entrega_conductor");
        }

        return (
            <article className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 bg-gradient-to-r from-slate-800 to-slate-700 px-3 py-2.5 text-white">
                    <div className="flex items-center gap-2 min-w-0">
                        <span className="rounded-md bg-white/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                            #{index + 1}
                        </span>
                        <span className="text-sm font-bold tabular-nums truncate">{placa}</span>
                        <span className="hidden sm:inline text-white/50">·</span>
                        <span className="text-xs font-medium tabular-nums truncate opacity-90">
                            {displayValue(item.numeroDocumento)}
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        <Badge tone={finalizadoTone(item)}>{displayValue(item.finalizadoLabel)}</Badge>
                        {item.estadoPago && (
                            <Badge tone={estadoPagoTone(item.estadoPago)}>{item.estadoPago}</Badge>
                        )}
                    </div>
                </div>
                <div className="divide-y divide-gray-100 px-3 py-1">
                    <Cell label={t("column_almacen")}>
                        <span className="inline-flex items-start gap-2">
                            <span className="font-medium">{displayValue(item.almacen)}</span>
                        </span>
                    </Cell>
                    <Cell label={t("label_cliente")}>
                        <span className="inline-flex items-start gap-2">
                            {clienteColor && (
                                <span
                                    className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ring-2 ring-white shadow"
                                    style={{ backgroundColor: clienteColor }}
                                    aria-hidden
                                />
                            )}
                            <span className="font-medium">{displayValue(item.cliente)}</span>
                        </span>
                    </Cell>
                    <Cell label={t("label_geocerca")}>{displayValue(item.geocerca)}</Cell>
                    <Cell label={t("label_tipo_servicio")}>
                        {item.tipoServicio ? (
                            <Badge tone="info">{item.tipoServicio}</Badge>
                        ) : (
                            "—"
                        )}
                    </Cell>
                    <Cell label={t("column_fecha_entrega_programada")}>
                        <span className="tabular-nums">{displayValue(item.fechaEntregaProgramada)}</span>
                    </Cell>
                    <Cell label={t("column_fecha_entrega_excel")}>
                        <span className="tabular-nums">{displayValue(item.fechaEntregaExcel)}</span>
                    </Cell>
                    <Cell label={t("column_fecha_carguio")}>
                        <span className="tabular-nums">{displayValue(item.fechaCarguio)}</span>
                    </Cell>
                    <Cell label={t("column_fecha_programacion_salida")}>
                        <span className="tabular-nums">{displayValue(item.fechaProgramacionSalida)}</span>
                    </Cell>

                    <Cell label={fechaProceso}>
                        <span className="tabular-nums">{displayValue(item.fechaProcesoWorkflow)}</span>
                    </Cell>


                    <Cell label={t("column_coordenadas_cliente")} mono>
                        {formatCoordenadas(item.coordenadasCliente)}
                    </Cell>
                    <Cell label={t("column_coordenadas_inicio_atencion")} mono>
                        {formatCoordenadas(item.coordenadasInicioAtencion)}
                    </Cell>
                    <Cell label={t("column_coordenadas_fin_atencion")} mono>
                        {formatCoordenadas(item.coordenadasFinAtencion)}
                    </Cell>
                    <Cell label={t("column_fecha_hora_inicio_atencion")} mono>
                        {(item.fechaHoraInicioAtencion)}
                    </Cell>
                    <Cell label={t("column_fecha_hora_fin_atencion")} mono>
                        {(item.fechaHoraFinAtencion)}
                    </Cell>
                    <Cell label={t("column_nombre_conductor")} mono>
                        {displayValue(item.nombreConductor)}
                    </Cell>
                </div>
                <ProductosEntregasPanel
                    item={item}
                    expanded={productosExpanded}
                    onToggle={() => toggleProductos(rowKey)}
                    t={t}
                />
            </article>
        );
    };

    const workflowLabelsUi = {
        estadoAsignado: t("label_estado_asignado"),
        estadoEnProceso: t("label_estado_en_proceso"),
        empty: "—",
    };

    const renderObtenerEstado = (workflow) => {
        const text = obtenerEstadoWorkflow(workflow, workflowLabelsUi);
        return text === "" ? "—" : text;
    };

    const renderObtenerSubEstado = (workflow) => {
        const text = obtenerSubEstadoWorkflow(workflow, workflowLabelsUi);
        return text === "" ? "—" : text;
    };

    const renderEstadoPago = (workflow) => {
        const { descripcion, detalle } = obtenerEstadoPagoWorkflow(workflow);
        if (!descripcion && !detalle) return "—";
        return (
            <>
                {descripcion && (
                    <Badge tone={estadoPagoTone(descripcion)}>{descripcion}</Badge>
                )}
                {detalle && (
                    <p className="mt-1 text-xs text-gray-600 break-words">{detalle}</p>
                )}
            </>
        );
    };

    /**
     * Desktop: delivery row + expandable productos row.
     */
    const TableEntregasRowDesktop = ({ item, index, rowKey }) => {
        const placa = item.labelPlaca ?? item.valuePlaca ?? "—";
        const almacen = item.almacen ?? "—";
        const clienteColor =
            item.colorCliente && String(item.colorCliente).trim() !== ""
                ? String(item.colorCliente)
                : null;

        const td =
            "px-3 py-3 align-top text-sm text-gray-900 border-b border-gray-100 group-last:border-b-0";
        const thStyle = "whitespace-nowrap";
        const productosExpanded = expandedProductos.has(rowKey);

        return (
            <>
                <tr className="group bg-white even:bg-slate-50/80 hover:bg-blue-50/50 transition-colors">
                    <td className={`${td} w-10 text-center text-xs font-semibold text-gray-500 tabular-nums`}>
                        {index + 1}
                    </td>
                    <td className={`${td} font-medium tabular-nums ${thStyle}`}>
                        {almacen}
                    </td>
                    <td className={`${td} ${thStyle}`}>
                        <span className="inline-flex rounded-md bg-slate-900 px-2 py-1 text-xs font-bold text-white tabular-nums">
                            {placa}
                        </span>
                    </td>
                    <td className={`${td} font-medium tabular-nums ${thStyle}`}>
                        {displayValue(item.numeroDocumento)}
                    </td>
                    <td className={`${td} min-w-[10rem] max-w-[14rem]`}>
                        <span className="inline-flex items-start gap-2">
                            {clienteColor && (
                                <span
                                    className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full ring-2 ring-white shadow"
                                    style={{ backgroundColor: clienteColor }}
                                    aria-hidden
                                />
                            )}
                            <span className="break-words leading-snug">{displayValue(item.cliente)}</span>
                        </span>
                    </td>
                    <td className={`${td} max-w-[8rem] break-words text-gray-700`}>
                        {displayValue(item.geocerca)}
                    </td>
                    {/* <td className={td}>
                    {item.tipoServicio ? (
                        <Badge tone="info">{item.tipoServicio}</Badge>
                    ) : (
                        "—"
                    )}
                </td> */}
                    {/* <td className={td}>
                    {item.estadoPago ? (
                        <Badge tone={estadoPagoTone(item.estadoPago)}>{item.estadoPago}</Badge>
                    ) : (
                        "—"
                    )}
                </td> */}
                    <td className={td}>
                        {item.workflow ? (
                            renderObtenerEstado(item.workflow)
                        ) : (
                            "—"
                        )}
                    </td>
                    <td className={td}>
                        {item.workflow ? (
                            renderObtenerSubEstado(item.workflow)
                        ) : (
                            "—"
                        )}
                    </td>
                    <td className={td}>
                        <Badge tone={finalizadoTone(item)}>
                            {displayValue(item.finalizadoLabel)}
                        </Badge>
                    </td>
                    <td className={td}>
                        {item.workflow ? (
                            renderEstadoPago(item.workflow)
                        ) : (
                            "—"
                        )}
                    </td>

                    <td className={`${td} whitespace-nowrap tabular-nums text-gray-700 text-xs`}>
                        {displayValue(item.fechaEntregaProgramada)}
                    </td>
                    <td className={`${td} whitespace-nowrap tabular-nums text-gray-700 text-xs`}>
                        {displayValue(item.fechaEntregaExcel)}
                    </td>
                    <td className={`${td} whitespace-nowrap tabular-nums text-gray-700 text-xs`}>
                        {displayValue(item.fechaCarguio)}
                    </td>
                    <td className={`${td} whitespace-nowrap tabular-nums text-gray-700 text-xs`}>
                        {displayValue(item.fechaProgramacionSalida)}
                    </td>
                    <td className={`${td} whitespace-nowrap tabular-nums text-gray-700 text-xs`}>
                        {displayValue(item.fechaProcesoWorkflow)}
                    </td>

                    <td className={`${td} font-mono text-[11px] text-gray-700 max-w-[9rem] break-all`}>
                        {formatCoordenadas(item.coordenadasCliente)}
                    </td>
                    {/* <td className={`${td} font-mono text-[11px] text-gray-700 max-w-[9rem] break-all`}>
                        {formatCoordenadas(item.coordenadasInicioAtencion)}
                    </td>
                    <td className={`${td} font-mono text-[11px] text-gray-700 max-w-[9rem] break-all`}>
                        {formatCoordenadas(item.coordenadasFinAtencion)}
                    </td> */}
                    <td className={`${td} min-w-[180px] max-w-[220px] text-xs text-gray-700`}>
                        <div className="truncate" title={displayValue(item.fechaHoraInicioAtencion)}>
                            {displayValue(item.fechaHoraInicioAtencion)}
                        </div>
                    </td>
                    <td className={`${td} min-w-[180px] max-w-[220px] text-xs text-gray-700`}>
                        <div className="truncate" title={displayValue(item.fechaHoraFinAtencion)}>
                            {displayValue(item.fechaHoraFinAtencion)}
                        </div>
                    </td>
                    <td className={`${td} min-w-[180px] max-w-[220px] text-xs text-gray-700`}>
                        <div className="break-words" title={displayValue(item.nombreConductor)}>
                            {displayValue(item.nombreConductor)}
                        </div>
                    </td>
                </tr>
                <tr className="bg-slate-50/40">
                    <td colSpan={DESKTOP_COLUMN_COUNT} className="p-0 align-top border-b border-gray-100">
                        <ProductosEntregasPanel
                            item={item}
                            expanded={productosExpanded}
                            onToggle={() => toggleProductos(rowKey)}
                            t={t}
                        />
                    </td>
                </tr>
                <tr aria-hidden="true" data-export-ignore="true" className="pointer-events-none">
                    <td
                        colSpan={DESKTOP_COLUMN_COUNT}
                        className="h-4 p-0 border-0 bg-gray-50/50"
                    />
                </tr>
            </>
        );
    };

    const exportEntregasPdf = async (rows) => {
        // Example:
        // await sharedExportarPdf(tableContentRef, "resumen-entregas", {
        //   onStart: () => setExporting(true),
        //   onFinish: () => setExporting(false),
        // });
        console.log("[exportEntregasPdf] rows to export:", rows);
    };

    const render = () => {
        try {
            let fechaProceso = t("column_fecha_proceso");
            if (idEmpresa === ID_EMPRESA.ORANGE) {
                fechaProceso = t("column_fecha_entrega_conductor");
            }
            // console.log('idEmpresa', idEmpresa);
            // console.log('type of idEmpresa', typeof idEmpresa);
            // console.log('ID_EMPRESA.ORANGE', typeof ID_EMPRESA.ORANGE);
            // console.log(fechaProceso);
            return (
                <div className="mt-4 w-full">
                    <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                        <div className="relative flex-1 min-w-0 max-w-xl">
                            <Search
                                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                                aria-hidden
                            />
                            <input
                                type="search"
                                value={filterQuery}
                                onChange={(e) => setFilterQuery(e.target.value)}
                                placeholder={t("grilla_placeholderbuscar")}
                                className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-10 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                aria-label={t("label_buscar")}
                            />
                            {hasFilter && (
                                <button
                                    type="button"
                                    onClick={() => setFilterQuery("")}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                                    aria-label={t("label_cerrar")}
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>

                        <div className="flex items-center justify-between gap-2 sm:justify-end sm:shrink-0">
                            <p className="text-xs text-gray-500 tabular-nums">
                                {hasFilter ? (
                                    <>
                                        <span className="font-semibold text-gray-700">{shown}</span>
                                        {" / "}
                                        {total}
                                    </>
                                ) : (
                                    <span className="font-semibold text-gray-700">{total}</span>
                                )}{" "}
                                {t("label_registros")}
                            </p>

                            <div className="relative">
                                <button
                                    ref={exportButtonRef}
                                    type="button"
                                    onClick={() => setExportMenuOpen((open) => !open)}
                                    disabled={!canExport}
                                    className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                    aria-expanded={exportMenuOpen}
                                    aria-haspopup="menu"
                                >
                                    <Download className="h-4 w-4 shrink-0" aria-hidden />
                                    {t("label_exportar")}
                                    <ChevronDown
                                        className={`h-4 w-4 shrink-0 transition-transform ${exportMenuOpen ? "rotate-180" : ""}`}
                                        aria-hidden
                                    />
                                </button>
                                {exportMenuOpen && canExport && (
                                    <div
                                        ref={exportMenuRef}
                                        role="menu"
                                        className="absolute right-0 z-50 mt-1 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
                                    >
                                        {/* <button
                                        type="button"
                                        role="menuitem"
                                        onClick={handleExportPdfClick}
                                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                        <Download className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
                                        {t("label_exportar_pdf")}
                                    </button> */}
                                        {btnExportar === true && (
                                            <button
                                                type="button"
                                                role="menuitem"
                                                onClick={handleExportExcelClick}
                                                className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                            >
                                                <FileSpreadsheet className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
                                                {t("label_exportar_excel")}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {shown === 0 ? (
                        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center text-sm text-gray-600">
                            {t("label_sin_datos")}
                        </div>
                    ) : (
                        <>
                            {/* Mobile + iPad portrait: stacked cards with 2-col grid */}
                            <div className="space-y-3 lg:hidden">
                                {filteredItems.map((item, index) => {
                                    const rowKey = getItemKey(item, index);
                                    return (
                                        <TableEntregasCardMobile
                                            key={rowKey}
                                            item={item}
                                            index={index}
                                            rowKey={rowKey}
                                        />
                                    );
                                })}
                            </div>

                            {/* Desktop / iPad landscape: scrollable data table */}
                            <div className="hidden lg:block overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
                                <table className="w-full min-w-[1200px] border-collapse text-left">
                                    <thead>
                                        <tr className="border-b border-gray-200 bg-gradient-to-r from-slate-100 to-slate-50">
                                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-600 w-10 text-center">
                                                #
                                            </th>
                                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-600 whitespace-nowrap">
                                                {t("column_almacen")}
                                            </th>
                                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-600 whitespace-nowrap">
                                                {t("label_placa")}
                                            </th>
                                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-600 whitespace-nowrap">
                                                {t("label_numero_documento")}
                                            </th>
                                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-600 min-w-[10rem]">
                                                {t("label_cliente")}
                                            </th>
                                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-600">
                                                {t("label_geocerca")}
                                            </th>
                                            {/* <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-600">
                                            {t("label_tipo_servicio")}
                                        </th> */}
                                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-600">
                                                {t("label_estado")}
                                            </th>
                                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-600">
                                                {t("label_sub_estado")}
                                            </th>
                                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-600">
                                                {t("label_finalizado")}
                                            </th>
                                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-600 min-w-[10rem]">
                                                {t("label_estado_pago")}
                                            </th>

                                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-600 whitespace-nowrap">
                                                {t("column_fecha_entrega_programada")}
                                            </th>
                                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-600 whitespace-nowrap">
                                                {t("column_fecha_entrega_excel")}
                                            </th>
                                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-600 whitespace-nowrap">
                                                {t("column_fecha_carguio")}
                                            </th>
                                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-600 whitespace-nowrap">
                                                {t("column_fecha_programacion_salida")}
                                            </th>
                                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-600 whitespace-nowrap">
                                                {fechaProceso}
                                            </th>

                                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-600">
                                                {t("column_coordenadas_cliente")}
                                            </th>
                                            {/* <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-600">
                                            {t("column_coordenadas_inicio_atencion")}
                                        </th>
                                        <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-600">
                                            {t("column_coordenadas_fin_atencion")}
                                        </th> */}
                                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-600 min-w-[180px] max-w-[220px] truncate">
                                                {t("column_fecha_hora_inicio_atencion")}
                                            </th>
                                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-600 min-w-[180px] max-w-[220px] truncate">
                                                {t("column_fecha_hora_fin_atencion")}
                                            </th>
                                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-600 min-w-[180px] max-w-[220px] break-words">
                                                {t("column_nombre_conductor")}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredItems.map((item, index) => {
                                            const rowKey = getItemKey(item, index);
                                            return (
                                                <TableEntregasRowDesktop
                                                    key={rowKey}
                                                    item={item}
                                                    index={index}
                                                    rowKey={rowKey}
                                                />
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            );
        } catch (error) {
            console.log('render err');
            console.log(error);
            return (
                <div>
                    <h1>TableEntregasTable</h1>
                </div>
            );
        }
    };

    return render();
}